/**
 * Session Timer Service
 *
 * Tracks session expiration time and emits warnings at configurable thresholds.
 * Handles session refresh and integrates with activity monitoring.
 */
import { getSessionConfig } from '../config/sessionConfig';
import { activityMonitor } from './ActivityMonitorService';
import { logger } from "../utils/logger";
import { apiClient } from "../utils/apiClient";
// import { logger } from "../utils/logger";
export interface SessionInfo {
    expiresAt: string; // ISO timestamp
    expiresIn: number; // seconds remaining
    maxAge: number; // total session duration
    isAuthenticated: boolean;
}
export enum SessionWarningLevel {
    NONE = 'none',
    PRIMARY = 'primary',// 5 minutes
    CRITICAL = 'critical',// 1 minute
    FINAL = 'final' // 30 seconds
}
type WarningCallback = (timeRemaining: number, level: SessionWarningLevel) => void;
type ExpiringCallback = (timeRemaining: number) => void;
type ExpiredCallback = () => void;
type RefreshedCallback = (newExpiresAt: Date) => void;
class SessionTimerService {
    private static instance: SessionTimerService;
    private expirationTime: Date | null = null;
    private checkInterval: NodeJS.Timeout | null = null;
    private isMonitoring: boolean = false;
    private currentWarningLevel: SessionWarningLevel = SessionWarningLevel.NONE;
    private lastRefreshTime: number = 0;
    private lastKeepaliveTime: number = 0;
    // Callbacks
    private warningListeners: Set<WarningCallback> = new Set();
    private expiringListeners: Set<ExpiringCallback> = new Set();
    private expiredListeners: Set<ExpiredCallback> = new Set();
    private refreshedListeners: Set<RefreshedCallback> = new Set();
    // Activity monitoring
    private activityUnsubscribe: (() => void) | null = null;
    private constructor() { }
    public static getInstance(): SessionTimerService {
        if (!SessionTimerService.instance) {
            SessionTimerService.instance = new SessionTimerService();
        }
        return SessionTimerService.instance;
    }
    /**
     * Initialize session monitoring
     */
    public async initialize(): Promise<void> {
        try {
            // Check if there's a session cookie before attempting to fetch session info
            // This prevents unnecessary 401 errors for unauthenticated users
            if (!this.hasSessionCookie()) {
                logger.security('⏱️ No session cookie found, skipping session timer initialization');
                return;
            }
            logger.auth('⏱️ Initializing session timer');
            // Fetch session info from server
            const sessionInfo = await this.fetchSessionInfo();
            if (!sessionInfo) {
                logger.auth('⏱️ No active session found');
                return;
            }
            this.expirationTime = new Date(sessionInfo.expiresAt);
            logger.auth('⏱️ Session expires at:', { "arg0": this.expirationTime.toISOString() });
            // Start monitoring
            this.startMonitoring();
            // Start activity monitoring
            await this.startActivityMonitoring();
        }
        catch (error) {
            logger.auth('⏱️ Failed to initialize session timer:', { "arg0": error });
        }
    }
    /**
     * Start monitoring session time
     */
    public async startMonitoring(): Promise<void> {
        if (this.isMonitoring) {
            return;
        }
        const config = await getSessionConfig();
        logger.auth('⏱️ Session monitoring started');
        this.isMonitoring = true;
        // Check immediately
        this.checkSessionTime();
        // Set up interval
        this.checkInterval = setInterval(() => {
            this.checkSessionTime();
        }, config.checkInterval);
    }
    /**
     * Stop monitoring session time
     */
    public stopMonitoring(): void {
        if (!this.isMonitoring) {
            return;
        }
        logger.auth('⏱️ Session monitoring stopped');
        this.isMonitoring = false;
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
            this.checkInterval = null;
        }
        // Stop activity monitoring
        if (this.activityUnsubscribe) {
            this.activityUnsubscribe();
            this.activityUnsubscribe = null;
        }
        activityMonitor.stopMonitoring();
    }
    /**
     * Get time remaining until session expires (milliseconds)
     */
    public getTimeRemaining(): number {
        if (!this.expirationTime) {
            return 0;
        }
        return Math.max(0, this.expirationTime.getTime() - Date.now());
    }
    /**
     * Get time remaining in seconds
     */
    public getTimeRemainingSeconds(): number {
        return Math.floor(this.getTimeRemaining() / 1000);
    }
    /**
     * Get current warning level
     */
    public getWarningLevel(): SessionWarningLevel {
        return this.currentWarningLevel;
    }
    /**
     * Refresh session
     */
    public async refresh(): Promise<boolean> {
        try {
            const now = Date.now();
            const config = await getSessionConfig();
            // Check cooldown
            if (now - this.lastRefreshTime < config.refreshCooldown) {
                logger.auth('⏱️ Session refresh cooldown active');
                return false;
            }
            // Don't refresh if < 30 seconds remaining
            const timeRemaining = this.getTimeRemaining();
            if (timeRemaining < 30000) {
                logger.debug('⏱️ Too close to expiration to refresh');
                return false;
            }
            logger.auth('⏱️ Refreshing session');
            const response = await apiClient.post<any>('/session/refresh', {}, {
                requiresAuth: true
            });
            if (!response.success) {
                logger.auth('⏱️ Session refresh failed:', { "arg0": response.error });
                return false;
            }
            const expiresAt = this.extractExpiresAtFromRefreshResponse(response);
            if (!expiresAt) {
                logger.auth('⏱️ Session refresh response missing expiresAt', { "arg0": response.data });
                return false;
            }
            this.expirationTime = expiresAt;
            this.lastRefreshTime = now;
            this.currentWarningLevel = SessionWarningLevel.NONE;
            logger.auth('⏱️ Session refreshed, new expiration:', { "arg0": this.expirationTime.toISOString() });
            // Notify listeners
            this.refreshedListeners.forEach(callback => {
                try {
                    callback(this.expirationTime!);
                }
                catch (error) {
                    logger.error('Error in refreshed callback:', error, {});
                }
            });
            return true;
        }
        catch (error) {
            logger.auth('⏱️ Session refresh error:', { "arg0": error });
            return false;
        }
    }

    /**
     * Normalize refresh response formats.
     * Backend returns: { success: true, data: { expiresAt: number } }
     * Some callers/services wrap: { success: true, data: { success: true, data: { expiresAt: string|number } } }
     */
    private extractExpiresAtFromRefreshResponse(response: any): Date | null {
        const unwrap = (value: any): any => {
            if (!value || typeof value !== 'object') {
                return value;
            }
            // unwrap one level of { success, data }
            if ('success' in value && value.success === true && 'data' in value) {
                return (value as any).data;
            }
            return value;
        };

        const a = unwrap(response);
        const b = unwrap(a?.data);
        const candidate =
            (typeof b === 'object' && b ? (b as any).expiresAt : undefined) ??
            (typeof a === 'object' && a ? (a as any).expiresAt : undefined);

        if (typeof candidate === 'number' && Number.isFinite(candidate)) {
            return new Date(candidate);
        }
        if (typeof candidate === 'string' && candidate.trim().length > 0) {
            const d = new Date(candidate);
            if (!Number.isNaN(d.getTime())) {
                return d;
            }
        }
        return null;
    }
    /**
     * Register warning callback
     */
    public onWarning(callback: WarningCallback): () => void {
        this.warningListeners.add(callback);
        return () => this.warningListeners.delete(callback);
    }
    /**
     * Register expiring callback
     */
    public onExpiring(callback: ExpiringCallback): () => void {
        this.expiringListeners.add(callback);
        return () => this.expiringListeners.delete(callback);
    }
    /**
     * Register expired callback
     */
    public onExpired(callback: ExpiredCallback): () => void {
        this.expiredListeners.add(callback);
        return () => this.expiredListeners.delete(callback);
    }
    /**
     * Register refreshed callback
     */
    public onRefreshed(callback: RefreshedCallback): () => void {
        this.refreshedListeners.add(callback);
        return () => this.refreshedListeners.delete(callback);
    }
    /**
     * Check if a session cookie exists
     */
    private hasSessionCookie(): boolean {
        if (typeof document === 'undefined') {
            return false;
        }
        // Check for session cookie names used by the backend
        const cookies = document.cookie.split(';').map(c => c.trim());
        return cookies.some(cookie => cookie.startsWith('sid=') ||
            cookie.startsWith('connect.sid=') ||
            cookie.startsWith('apt_session='));
    }
    /**
     * Fetch session info from server
     */
    private async fetchSessionInfo(): Promise<SessionInfo | null> {
        try {
            const response = await apiClient.get<SessionInfo | {
                success: boolean;
                data?: SessionInfo;
            }>('/session/info', {
                requiresAuth: false // Public endpoint to check session status
            });
            if (!response.success) {
                return null;
            }
            // Handle both direct SessionInfo and wrapped response formats
            const data = response.data;
            if (!data) {
                return null;
            }
            // If data is already SessionInfo (has expiresAt property)
            if (typeof data === 'object' && 'expiresAt' in data && 'expiresIn' in data) {
                return data as SessionInfo;
            }
            // If data is wrapped in { success, data }
            if (typeof data === 'object' && 'data' in data && (data as any).data) {
                return (data as any).data as SessionInfo;
            }
            return null;
        }
        catch (error) {
            logger.auth('⏱️ Failed to fetch session info:', { "arg0": error });
            return null;
        }
    }
    /**
     * Check session time and emit warnings
     */
    private async checkSessionTime(): Promise<void> {
        if (!this.expirationTime) {
            return;
        }
        const timeRemaining = this.getTimeRemaining();
        const config = await getSessionConfig();
        // Determine warning level
        let newLevel = SessionWarningLevel.NONE;
        if (timeRemaining <= 0) {
            newLevel = SessionWarningLevel.NONE;
            // Emit expired event
            this.expiredListeners.forEach(callback => {
                try {
                    callback();
                }
                catch (error) {
                    logger.error('Error in expired callback:', error, {});
                }
            });
            return;
        }
        else if (timeRemaining <= config.warningThresholds.final) {
            newLevel = SessionWarningLevel.FINAL;
        }
        else if (timeRemaining <= config.warningThresholds.critical) {
            newLevel = SessionWarningLevel.CRITICAL;
        }
        else if (timeRemaining <= config.warningThresholds.primary) {
            newLevel = SessionWarningLevel.PRIMARY;
        }
        // Emit warning if level changed
        if (newLevel !== this.currentWarningLevel && newLevel !== SessionWarningLevel.NONE) {
            logger.auth(`⏱️ Session warning: ${newLevel}, time remaining: ${Math.floor(timeRemaining / 1000)}s`);
            this.currentWarningLevel = newLevel;
            // Emit warning
            this.warningListeners.forEach(callback => {
                try {
                    callback(timeRemaining, newLevel);
                }
                catch (error) {
                    logger.error('Error in warning callback:', error, {});
                }
            });
        }
        // Emit expiring event for critical/final levels
        if (newLevel === SessionWarningLevel.CRITICAL || newLevel === SessionWarningLevel.FINAL) {
            this.expiringListeners.forEach(callback => {
                try {
                    callback(timeRemaining);
                }
                catch (error) {
                    logger.error('Error in expiring callback:', error, {});
                }
            });
        }
    }
    /**
     * Start monitoring user activity for auto-refresh
     */
    private async startActivityMonitoring(): Promise<void> {
        const config = await getSessionConfig();
        if (!config.autoRefreshEnabled) {
            return;
        }
        logger.debug('⏱️ Activity monitoring enabled for auto-refresh');
        activityMonitor.startMonitoring();
        this.activityUnsubscribe = activityMonitor.onActivity(async () => {
            await this.handleActivity();
        });
    }
    /**
     * Handle user activity (for auto-refresh)
     */
    private async handleActivity(): Promise<void> {
        const config = await getSessionConfig();
        if (!config.autoRefreshEnabled) {
            return;
        }
        const timeRemaining = this.getTimeRemaining();
        // Only auto-refresh if warning level is active
        if (this.currentWarningLevel !== SessionWarningLevel.NONE) {
            logger.warn('⏱️ User activity detected during warning, attempting refresh');
            this.refresh().then(success => {
                if (success) {
                    logger.auth('⏱️ Session auto-refreshed on activity');
                }
            });
        }
        // Otherwise, send a low-frequency keepalive so server-side idle timeout doesn't log out active users.
        // This updates `lastAccessedAt` on the server without changing session policy.
        else {
            const KEEPALIVE_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes
            const now = Date.now();
            if (now - this.lastKeepaliveTime < KEEPALIVE_COOLDOWN_MS) {
                return;
            }
            this.lastKeepaliveTime = now;
            const info = await this.fetchSessionInfo();
            if (info?.expiresAt) {
                const nextExpiry = new Date(info.expiresAt);
                if (!Number.isNaN(nextExpiry.getTime())) {
                    this.expirationTime = nextExpiry;
                }
            }
        }
    }
    /**
     * Get expiration time
     */
    public getExpirationTime(): Date | null {
        return this.expirationTime;
    }
}
// Export singleton instance
export const sessionTimer = SessionTimerService.getInstance();
export default sessionTimer;
